import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Loader2, Send, Navigation } from "lucide-react";
import { COURSES, PHONE, PHONE_LINK, EMAIL, ADDRESS, WHATSAPP_LINK, MAP_EMBED, DIRECTIONS_LINK } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", phone: "", email: "", course: COURSES[0].title, message: "" };

const inputClass =
  "w-full rounded-md border border-brand-line bg-brand-bg px-4 py-3 text-sm text-brand-ink outline-none transition-colors duration-300 placeholder:text-brand-muted/60 focus:border-brand-green";

export default function QueryForm() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      toast.success("Enquiry sent! Our counsellors will reach out within 24 hours.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please reach us on WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="mx-auto max-w-7xl px-6 py-28 md:py-36">
      <div className="grid gap-16 lg:grid-cols-12 lg:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-terra">
            Enquire · Free Counselling
          </p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl">
            Begin your <em className="italic text-brand-terra">journey</em>.
          </h2>
          <p className="mt-7 max-w-md text-base leading-relaxed text-brand-muted md:text-lg">
            Tell us where you want to go — our admissions counsellors will call
            you back within 24 hours with course details, fee plans and campus
            visit slots.
          </p>
          <ul className="mt-10 space-y-5 text-sm">
            <li className="flex items-center gap-4">
              <Phone className="h-5 w-5 shrink-0 text-brand-terra" />
              <a data-testid="contact-phone-link" href={PHONE_LINK} className="font-medium transition-colors hover:text-brand-terra">
                {PHONE}
              </a>
            </li>
            <li className="flex items-center gap-4">
              <Mail className="h-5 w-5 shrink-0 text-brand-terra" />
              <a data-testid="contact-email-link" href={`mailto:${EMAIL}`} className="font-medium transition-colors hover:text-brand-terra">
                {EMAIL}
              </a>
            </li>
            <li className="flex items-start gap-4">
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-terra" />
              <span data-testid="contact-address" className="font-medium leading-relaxed">{ADDRESS}</span>
            </li>
          </ul>
          <a
            data-testid="enquiry-whatsapp-link"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-bold text-white shadow-md transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#1DA851]"
          >
            <MessageCircle className="h-5 w-5" />
            Chat on WhatsApp
          </a>
          <div data-testid="campus-map" className="mt-10 max-w-md overflow-hidden rounded-md border border-brand-line">
            <iframe
              title="Abhi Institute of Hotel Management — Dwarka, New Delhi"
              src={MAP_EMBED}
              className="h-64 w-full grayscale-[35%] transition-[filter] duration-500 hover:grayscale-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <a
              data-testid="campus-directions-link"
              href={DIRECTIONS_LINK}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center justify-between bg-brand-surface px-5 py-4 text-sm font-semibold text-brand-green transition-colors duration-300 hover:bg-brand-green hover:text-white"
            >
              Get Directions to Campus
              <Navigation className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </motion.div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="border border-brand-line bg-brand-surface p-8 shadow-sm md:p-10 lg:col-span-7"
        >
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label htmlFor="enquiry-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                Full Name *
              </label>
              <input
                id="enquiry-name"
                data-testid="enquiry-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="enquiry-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                Phone *
              </label>
              <input
                id="enquiry-phone"
                data-testid="enquiry-phone-input"
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 ..."
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="enquiry-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                Email *
              </label>
              <input
                id="enquiry-email"
                data-testid="enquiry-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="enquiry-course" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                Course of Interest *
              </label>
              <select
                id="enquiry-course"
                data-testid="enquiry-course-select"
                required
                value={form.course}
                onChange={set("course")}
                className={inputClass}
              >
                {COURSES.map((c) => (
                  <option key={c.title} value={c.title}>
                    {c.title}
                  </option>
                ))}
                <option value="Not sure yet">Not sure yet — need guidance</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="enquiry-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                Message
              </label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Ask us anything — fees, hostel, placements..."
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <button
              data-testid="enquiry-submit-button"
              type="submit"
              disabled={sending}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-ink disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              )}
              {sending ? "Sending..." : "Send Enquiry"}
            </button>
            <p className="text-xs text-brand-muted">
              By submitting, you agree to be contacted by our admissions team.
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
